import { useState } from 'react';
import { Button, Menu, MenuItem } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';

import type {
  TestExecutionActionKey,
  TestExecutionActionOption,
} from '../utils/projectExecutionActions';

interface TestExecutionActionDropdownProps {
  options: TestExecutionActionOption[];
  disabled?: boolean;
  isExecutionInProgress?: boolean;
  buttonClassName?: string;
  menuClassName?: string;
  menuItemClassName?: string;
  defaultSelectedKey?: TestExecutionActionKey;
  ariaLabel?: string;
  onSelect: (action: TestExecutionActionKey) => void;
}

export default function TestExecutionActionDropdown({
  options,
  disabled = false,
  isExecutionInProgress = false,
  buttonClassName,
  menuClassName,
  menuItemClassName,
  defaultSelectedKey,
  ariaLabel = 'test execution actions',
  onSelect,
}: TestExecutionActionDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const initialKey =
    defaultSelectedKey && options.some(o => o.key === defaultSelectedKey)
      ? defaultSelectedKey
      : options[0]?.key;

  const [selectedKey, setSelectedKey] = useState<
    TestExecutionActionKey | undefined
  >(initialKey);

  const selectedOption = options.find(o => o.key === selectedKey) ?? options[0];

  const handleSelect = (action: TestExecutionActionKey) => {
    setSelectedKey(action);
    onSelect(action);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        aria-label={ariaLabel}
        aria-controls={anchorEl ? 'iqe-test-execution-actions-menu' : undefined}
        aria-haspopup="menu"
        onClick={e => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        className={buttonClassName}
        startIcon={<PlayCircleFilledIcon />}
        endIcon={<ArrowDropDownIcon />}
      >
        {selectedOption?.label}
      </Button>

      <Menu
        id="iqe-test-execution-actions-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        PaperProps={{
          className: menuClassName,
          style: { minWidth: anchorEl?.offsetWidth },
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.key}
            selected={option.key === selectedKey}
            onClick={() => handleSelect(option.key)}
            className={menuItemClassName}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
